import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames/bind";
import styles from "./ServicesPage.module.scss";
import { Package, Ruler, Shield, ExternalLink } from "lucide-react";
import useTranslations from "../../hooks/useTranslations";
import { useLanguageStore } from "../../stores/Language/useLanguageStore";

const cx = classNames.bind(styles);

const ServicesPage = () => {
    const location = useLocation();
    const currentTranslations = useTranslations();
    const currentLanguage = useLanguageStore((state) => state.currentLanguage);

    // Service content with bilingual support
    const serviceContent = currentTranslations?.ServicesPage || {
        title: "Dịch Vụ Khách Hàng",
        title_en: "Customer Services",
        subtitle: "Lianne Jewelry cam kết mang đến cho bạn trải nghiệm mua sắm tuyệt vời nhất",
        subtitle_en: "Lianne Jewelry is committed to providing you with the best shopping experience",
        
        order_tracking: {
            title: "Theo Dõi Đơn Hàng",
            title_en: "Order Tracking",
            description: "Theo dõi trạng thái đơn hàng của bạn một cách dễ dàng và thuận tiện. Bạn có thể kiểm tra tình trạng đơn hàng, thông tin vận chuyển và lịch sử mua hàng của mình.",
            description_en: "Track your order status easily and conveniently. You can check your order status, shipping information and purchase history.",
            link_text: "Đến Đơn Hàng Của Tôi",
            link_text_en: "Go to My Orders"
        },
        
        size_guide: {
            title: "Hướng Dẫn Đo Size Trang Sức",
            title_en: "Jewelry Size Guide",
            description: "Để chọn được size trang sức phù hợp nhất, hãy thực hiện theo hướng dẫn chi tiết dưới đây:",
            description_en: "To choose the most suitable jewelry size, please follow the detailed instructions below:",
            steps_title: "Các bước thực hiện:",
            steps_title_en: "Steps to follow:",
            steps: [
                {
                    title: "Chuẩn bị dụng cụ:",
                    title_en: "Prepare tools:",
                    content: "Một cây thước, một cây kéo, một cây bút và một tờ giấy.",
                    content_en: "A ruler, a pair of scissors, a pen, and a sheet of paper."
                },
                {
                    title: "Cắt giấy:",
                    title_en: "Cut paper:",
                    content: "Cắt một mảnh giấy dài khoảng 10cm và rộng 1cm.",
                    content_en: "Cut a strip of paper approximately 10cm long and 1cm wide."
                },
                {
                    title: "Đo ngón tay:",
                    title_en: "Measure finger:",
                    content: "Sử dụng đoạn giấy vừa cắt để quấn sát quanh ngón tay muốn đo và đánh dấu điểm giao nhau.",
                    content_en: "Use the paper strip to wrap tightly around the finger you want to measure and mark the intersection point."
                },
                {
                    title: "Tính toán kích thước:",
                    title_en: "Calculate size:",
                    content: "Tháo ra dùng thước đo chiều dài của đoạn giấy từ điểm đầu cho đến phần đánh dấu. Lấy kết quả đo được chia cho 3.14. Sau đó đối chiếu với bảng size nhẫn.",
                    content_en: "Remove and use ruler to measure the paper length from start to marked point. Divide the result by 3.14, then compare with the ring size chart."
                }
            ]
        },
        
        jewelry_care: {
            title: "Bảo Quản Trang Sức",
            title_en: "Jewelry Care",
            intro: "Trang sức không chỉ là món phụ kiện tôn vinh vẻ đẹp mà còn là tài sản quý giá, gắn liền với những kỷ niệm và cảm xúc. Để giữ cho sản phẩm luôn sáng bóng và bền đẹp theo thời gian, Lianne Jewelry xin gửi đến bạn hướng dẫn bảo quản chi tiết cho từng loại trang sức.",
            intro_en: "Jewelry is not only an accessory that enhances beauty but also a valuable asset, associated with memories and emotions. To keep your products shiny and beautiful over time, Lianne Jewelry provides detailed care instructions for each type of jewelry.",
            tips_title: "Mẹo bảo quản chung:",
            tips_title_en: "General care tips:",
            tips: [
                {
                    text: "Luôn tháo trang sức khi tắm, bơi lội hoặc chơi thể thao.",
                    text_en: "Always remove jewelry when bathing, swimming or playing sports."
                },
                {
                    text: "Tránh tiếp xúc trực tiếp với mỹ phẩm, nước hoa và các loại hóa chất.",
                    text_en: "Avoid direct contact with cosmetics, perfumes and chemicals."
                },
                {
                    text: "Khi không sử dụng, cất giữ trong hộp trang sức chuyên dụng của Lianne Jewelry để hạn chế trầy xước.",
                    text_en: "When not in use, store in Lianne Jewelry's specialized jewelry box to limit scratches."
                },
                {
                    text: "Vệ sinh định kỳ 1–2 tháng/lần và mang đến cửa hàng để được chăm sóc, làm sạch và đánh bóng chuyên nghiệp.",
                    text_en: "Clean regularly every 1–2 months and bring to store for professional care, cleaning and polishing."
                }
            ]
        }
    };

    useEffect(() => {
        // Xử lý scroll đến phần cụ thể khi có hash trong URL
        if (location.hash) {
            setTimeout(() => {
                const element = document.querySelector(location.hash);
                if (element) {
                    element.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                }
            }, 100);
        }
    }, [location.hash]);

    return (
        <div className={cx("container")}>
            <div className={cx("container__header")}>
                <h1>{currentLanguage === 'vi' ? serviceContent.title : serviceContent.title_en}</h1>
                <p>{currentLanguage === 'vi' ? serviceContent.subtitle : serviceContent.subtitle_en}</p>
            </div>

            <div className={cx("container__services")}>
                {/* Theo Dõi Đơn Hàng */}
                <div className={cx("service")}>
                    <div className={cx("service__header")}>
                        <Package className={cx("service__headerIcon")} />
                        <h2>{currentLanguage === 'vi' ? serviceContent.order_tracking.title : serviceContent.order_tracking.title_en}</h2>
                    </div>
                    <div className={cx("service__content")}>
                        <p>
                            {currentLanguage === 'vi' ? serviceContent.order_tracking.description : serviceContent.order_tracking.description_en}
                        </p>
                        <Link to="/profile?tab=orders" className={cx("trackOrderLink")}>
                            {currentLanguage === 'vi' ? serviceContent.order_tracking.link_text : serviceContent.order_tracking.link_text_en}
                            <ExternalLink />
                        </Link>
                    </div>
                </div>

                {/* Hướng Dẫn Đo Size */}
                <div id="size-guide" className={cx("service")}>
                    <div className={cx("service__header")}>
                        <Ruler className={cx("service__headerIcon")} />
                        <h2>{currentLanguage === 'vi' ? serviceContent.size_guide.title : serviceContent.size_guide.title_en}</h2>
                    </div>
                    <div className={cx("service__content", "sizeGuide")}>
                        <p>
                            {currentLanguage === 'vi' ? serviceContent.size_guide.description : serviceContent.size_guide.description_en}
                        </p>
                        
                        <div className={cx("sizeGuide__steps")}>
                            <h4 className={cx("sizeGuide__stepsTitle")}>
                                {currentLanguage === 'vi' ? serviceContent.size_guide.steps_title : serviceContent.size_guide.steps_title_en}
                            </h4>
                            <ul className={cx("sizeGuide__stepsList")}>
                                {serviceContent.size_guide.steps.map((step, index) => (
                                    <li key={index} data-step={index + 1}>
                                        <strong>
                                            {currentLanguage === 'vi' ? step.title : step.title_en}
                                        </strong>{' '}
                                        {currentLanguage === 'vi' ? step.content : step.content_en}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bảo Quản Trang Sức */}
                <div id="jewelry-care" className={cx("service")}>
                    <div className={cx("service__header")}>
                        <Shield className={cx("service__headerIcon")} />
                        <h2>{currentLanguage === 'vi' ? serviceContent.jewelry_care.title : serviceContent.jewelry_care.title_en}</h2>
                    </div>
                    <div className={cx("service__content", "careGuide")}>
                        <p>
                            {currentLanguage === 'vi' ? serviceContent.jewelry_care.intro : serviceContent.jewelry_care.intro_en}
                        </p>

                        <div className={cx("careGuide__tips")}>
                            <h4>{currentLanguage === 'vi' ? serviceContent.jewelry_care.tips_title : serviceContent.jewelry_care.tips_title_en}</h4>
                            <ul>
                                {serviceContent.jewelry_care.tips.map((tip, index) => (
                                    <li key={index}>
                                        {currentLanguage === 'vi' ? tip.text : tip.text_en}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServicesPage;
